import { useState } from 'react'
import moment from 'moment'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'

import { FlsaStatus, PaymentUnit } from '../employeeJob.slice'
import { DetailsRow } from './JobPayTab'
import {
  postCreateEmployeeJob,
  putUpdateEmployeeJob,
  putUpdateJobCompensation,
} from '../payrollActions'
import {
  selectEmployeeByUuid,
  selectJobByEmployeeUuid,
} from '../payroll.selectors'
import {
  flsaMapping,
  flsaDropdownOptions,
  useEmployeeWorkLocations,
} from '../helpers'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Button, Text, Form, Dialog, Card, CardColors } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Gusto_EmployeeJob } from '../generated_gusto_types'

// TODO: Migrate to V2
import {
  makeReqStringSchema,
  makeNumberSchema,
  getFieldNames,
} from '@/componentsV1/BaseComponents'
import { TypographyStyles } from '@/styles'

const END_VAL = 'for this employee'

const EditJobModal = ({
  uuid,
  job,
  closeModal,
}: {
  uuid: string
  job: Gusto_EmployeeJob | null
  closeModal: () => void
}) => {
  const dispatch = useAppDispatch()
  const compensation = job?.compensations[0]

  const { locationOptions, defaultAddress, setWorkLocation } =
    useEmployeeWorkLocations(uuid)

  const formik = useFormik({
    initialValues: {
      title: job?.title || '',
      rate: compensation?.rate || '0',
      flsa_status: compensation?.flsa_status || FlsaStatus.Exempt,
      payment_unit: compensation?.payment_unit || PaymentUnit.Year,
      location_uuid: defaultAddress || '',
    },
    enableReinitialize: true,
    onSubmit: async ({
      title,
      rate,
      payment_unit,
      flsa_status,
      location_uuid,
    }) => {
      // Set work address
      const setWorkAddressSuccess = await setWorkLocation(location_uuid)

      if (!setWorkAddressSuccess) {
        return
      }

      let jobRes

      if (job?.uuid && job.version) {
        jobRes = await putUpdateEmployeeJob(job?.uuid, {
          version: job.version,
          hire_date: job?.hire_date,
          title,
        })(dispatch)
      } else {
        jobRes = await postCreateEmployeeJob(uuid, {
          hire_date: moment().format(DATE_FORMATS.GUSTO_SUBMIT),
          title,
        })(dispatch)
      }

      const currentComp = jobRes?.compensations.find(
        (comp) => comp.uuid === jobRes?.current_compensation_uuid
      )

      if (!jobRes || !currentComp) {
        return
      }

      const compRes = await putUpdateJobCompensation(currentComp.uuid, {
        rate,
        payment_unit,
        flsa_status,
        version: currentComp.version,
      })(dispatch)

      if (compRes) {
        closeModal()
      }
    },
  })

  const { submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <Dialog open onClose={closeModal}>
      <Dialog.Title>Update Details</Dialog.Title>
      <Dialog.Content>
        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            <Form.Select
              name={fieldNames.flsa_status}
              options={flsaDropdownOptions}
              label="Employment Status"
              placeholder="Select Employment Status"
              required
              schema={makeReqStringSchema({
                field: 'employee type',
                end: END_VAL,
              })}
            />

            <Form.Input
              name={fieldNames.title}
              label="Job Title"
              required
              schema={makeReqStringSchema({ field: 'title', end: END_VAL })}
            />

            <Text className="form-label required">Compensation</Text>

            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
              <div className="sm:flex-1">
                <Form.Input
                  name={fieldNames.rate}
                  componentType="currency"
                  schema={makeNumberSchema({
                    field: 'rate',
                    allowedDecimals: 2,
                    end: END_VAL,
                  })}
                />
              </div>

              <div className="mt-2 sm:mt-0">
                <Text>Per</Text>
              </div>

              <div className="sm:flex-[0.6]">
                <Form.Select
                  name={fieldNames.payment_unit}
                  options={Object.values(PaymentUnit)
                    .filter((name) => name !== 'Paycheck')
                    .map((name) => ({
                      text: name,
                      value: name,
                    }))}
                  schema={makeReqStringSchema({
                    field: 'payment unit',
                    end: END_VAL,
                  })}
                />
              </div>
            </div>

            <Form.Select
              name={fieldNames.location_uuid}
              options={locationOptions}
              label="Work Location"
              required
              schema={yup.string().required('Work Location is required')}
            />
          </div>
          <Dialog.Footer className="mt-4">
            <Button onClick={submitForm} disabled={isSubmitting}>
              Confirm
            </Button>

            <Button onClick={closeModal} variant="secondary">
              Cancel
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}

export const EmployeeJobCompPanel = ({
  employeeUuid,
}: {
  employeeUuid: string
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const employee = useReselector(selectEmployeeByUuid, employeeUuid)
  const job = useReselector(selectJobByEmployeeUuid, employeeUuid)

  if (!employee) {
    return null
  }

  const comp = job?.compensations[0]

  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <Text variant={TypographyStyles.H2}>Compensation</Text>
          <Button onClick={() => setIsOpen(true)} variant="link">
            Edit
          </Button>
        </div>

        <DetailsRow
          label="Employee Type"
          value={comp?.flsa_status ? flsaMapping[comp.flsa_status] : ''}
        />
        <DetailsRow label="Job Title" value={job?.title} />
        {comp && (
          <DetailsRow
            label="Wage"
            value={`${formatCurrency(comp.rate)} per ${comp.payment_unit}`}
          />
        )}
      </div>

      {isOpen && (
        <EditJobModal
          closeModal={() => setIsOpen(false)}
          uuid={employee.uuid}
          job={job}
        />
      )}
    </Card>
  )
}
